"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import { workScopeStatusOptions } from "../../constants/options"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const BASE_FIELD_NAME = "home.plumbingInfo"

function HomePlumbingForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillPlumbingOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillPlumbingOn` })
    const goodwillPlumbingRoughInOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillPlumbingRoughInOn` })
    
    // Watch status fields and claimed by fields
    const plumbing = useWatch({ control, name: `${BASE_FIELD_NAME}.plumbing` })
    const plumbingClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const plumbingRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.plumbingRoughIn` })
    const plumbingRoughInClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.plumbingRoughInClaimedBy` })
    const goodwillPlumbing = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillPlumbing` })
    const goodwillPlumbingClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillPlumbingClaimedBy` })
    const goodwillPlumbingRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillPlumbingRoughIn` })
    const goodwillPlumbingRoughInClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillPlumbingRoughInClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: plumbing,
        claimedBy: plumbingClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: plumbingRoughIn,
        claimedBy: plumbingRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.plumbingRoughInClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillPlumbing,
        claimedBy: goodwillPlumbingClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillPlumbingClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillPlumbingOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillPlumbingRoughIn,
        claimedBy: goodwillPlumbingRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillPlumbingRoughInClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillPlumbingRoughInOn,
    })

    return (
        <HomeDetailsWrapperAccordion
            heading="Plumbing"
            wrapperClassName="xl:grid-cols-2"
        >
            <WorkScopeStatusSelect workScopeSlug="plumbing-fixtures"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Plumbing" toggleName={`${BASE_FIELD_NAME}.goodwillPlumbingOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.plumbing`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillPlumbingOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Plumbing Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillPlumbing`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Plumbing Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillPlumbingClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <WorkScopeStatusSelect workScopeSlug="rough-plumbing"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Plumbing Rough In" toggleName={`${BASE_FIELD_NAME}.goodwillPlumbingRoughInOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.plumbingRoughIn`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Plumbing Rough In Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.plumbingRoughInClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillPlumbingRoughInOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Plumbing Rough In Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillPlumbingRoughIn`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Plumbing Rough In Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillPlumbingRoughInClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the Plumbing work needed",
                    wrapperClassName: "xl:col-span-2"
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomePlumbingForm
